import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';

// ── Segments (/api/segments) — native query engine + saved segments (BOOSTT-3170) ─

// F6 (2026-08-19): field/operator lists verified against the live SegmentRule
// schema description on /openapi.json ("One of [...]") — the previous 4-field
// version was missing call_count/email/first_seen_at/last_seen_at/name/phone,
// and 'in'/'not_in' operators (confirmed by SegmentRule.value's description:
// "Comma-separated for 'in'/'not_in'").
export type SegmentFieldType = 'date' | 'string' | 'number';
export type SegmentField =
  | 'call_count'
  | 'customer_value_tier'
  | 'email'
  | 'first_seen_at'
  | 'last_contact_date'
  | 'last_seen_at'
  | 'last_service_date'
  | 'name'
  | 'phone'
  | 'service_type';
export type SegmentOperator = 'equals' | 'before' | 'after' | 'between' | 'in' | 'not_in';

export type SegmentFieldInfo = { field: SegmentField; type: SegmentFieldType; operators: SegmentOperator[] };

export type SegmentRule = {
  field: SegmentField;
  op: SegmentOperator;
  value?: string;
  value2?: string; // required for 'between'
};

/** Matches the live SegmentCriteriaModel: a rule list where an entry may
 * itself be one further nested group (max depth 2, per the schema). */
export type SegmentCriteria = {
  operator: 'AND' | 'OR';
  rules: SegmentCriteriaNode[];
};

export type SegmentCriteriaNode = SegmentRule | SegmentCriteria;

export function isSegmentGroup(node: SegmentCriteriaNode): node is SegmentCriteria {
  return 'rules' in node;
}

/** Matches the live SuppressionConfigModel exactly. */
export type SuppressionConfig = {
  dnc?: boolean;
  opted_out?: boolean;
  contacted_within_days?: number | null;
  active_deal?: boolean;
  upcoming_appointment?: boolean;
  exclude_segments?: string[];
};

export type SegmentContactSummary = {
  id: string;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  last_service_date?: string | null;
  last_contact_date?: string | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  call_count?: number | null;
  service_type?: string | null;
  customer_value_tier?: string | null;
};

export type SegmentQueryResult = {
  total: number;
  skip: number;
  limit: number;
  items: SegmentContactSummary[];
};

export type SavedSegment = {
  id: string;
  name: string;
  criteria: SegmentCriteria;
  suppressions?: SuppressionConfig | null;
  /** "defined" (never materialized) | "materialising" | "materialised" — what
   * the backend's launch-readiness Rule I4 actually reads (ISSUE_I4_SEGMENT_
   * MATERIALIZATION.md). record_count alone does NOT reflect this. */
  status: string;
  record_count: number;
  materialised_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type SegmentJobStatus = {
  id: string;
  job_type: string;
  /** "queued" | "processing" | "done" | "failed" | "exhausted" */
  status: string;
  attempts: number;
  last_error?: string | null;
  result?: Record<string, unknown> | null;
};

export async function fetchSegmentFields(axiosInstance?: AxiosInstance): Promise<SegmentFieldInfo[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<{ fields: SegmentFieldInfo[] }>(`${apiConfig.voiceEndpoints.segments}/fields`);
  return response.data.fields;
}

export async function querySegment(
  criteria: SegmentCriteria,
  params: { skip?: number; limit?: number } = {},
  axiosInstance?: AxiosInstance,
): Promise<SegmentQueryResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<SegmentQueryResult>(`${apiConfig.voiceEndpoints.segments}/query`, criteria, { params });
  return response.data;
}

export async function exportSegmentCsv(criteria: SegmentCriteria, axiosInstance?: AxiosInstance): Promise<Blob> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post(`${apiConfig.voiceEndpoints.segments}/query/export`, criteria, { responseType: 'blob' });
  return response.data;
}

export async function createSavedSegment(
  name: string,
  criteria: SegmentCriteria,
  suppressions?: SuppressionConfig,
  axiosInstance?: AxiosInstance,
): Promise<SavedSegment> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<SavedSegment>(apiConfig.voiceEndpoints.segments, { name, criteria, suppressions: suppressions ?? null });
  return response.data;
}

export async function fetchSavedSegments(axiosInstance?: AxiosInstance): Promise<SavedSegment[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<SavedSegment[]>(apiConfig.voiceEndpoints.segments);
  return response.data;
}

/** Live re-query/preview only — does NOT materialize the segment or move
 * Rule I4 (ISSUE_I4_SEGMENT_MATERIALIZATION.md). Use materializeAndWait for
 * that; keep using this one for actual live-count/preview needs (e.g. the
 * Audience step's row preview). */
export async function runSavedSegment(
  id: string,
  params: { skip?: number; limit?: number } = {},
  axiosInstance?: AxiosInstance,
): Promise<SegmentQueryResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<SegmentQueryResult>(`${apiConfig.voiceEndpoints.segments}/${id}/run`, {}, { params });
  return response.data;
}

export async function fetchSavedSegment(id: string, axiosInstance?: AxiosInstance): Promise<SavedSegment> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<SavedSegment>(`${apiConfig.voiceEndpoints.segments}/${id}`);
  return response.data;
}

/** `POST /{id}/materialise` — the REAL trigger for Rule I4. Async (202): flips
 * status to "materialising" and enqueues a background job; poll its result
 * with getSegmentJob (or materializeAndWait) rather than assuming it's done
 * when this call returns. */
export async function materializeSavedSegment(
  id: string,
  axiosInstance?: AxiosInstance,
): Promise<{ job_id: string; segment_id: string; status: string }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<{ job_id: string; segment_id: string; status: string }>(
    `${apiConfig.voiceEndpoints.segments}/${id}/materialise`, {},
  );
  return response.data;
}

export async function getSegmentJob(
  segmentId: string,
  jobId: string,
  axiosInstance?: AxiosInstance,
): Promise<SegmentJobStatus> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<SegmentJobStatus>(`${apiConfig.voiceEndpoints.segments}/${segmentId}/jobs/${jobId}`);
  return response.data;
}

const _TERMINAL_JOB_STATUSES = new Set(['done', 'failed', 'exhausted']);

/** Kicks off materialization and polls until the background job (main.py's
 * _segment_job_worker, ticks ~every 60s) reaches a terminal state, or the
 * timeout elapses. Returns the final SavedSegment (status "materialised" on
 * success) plus the job's terminal status, or null if it timed out without
 * finishing — the job keeps running server-side either way; the caller just
 * stopped waiting. */
export async function materializeAndWait(
  segmentId: string,
  axiosInstance?: AxiosInstance,
  opts: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<{ segment: SavedSegment; job: SegmentJobStatus } | null> {
  const intervalMs = opts.intervalMs ?? 3000;
  const timeoutMs = opts.timeoutMs ?? 90000;
  const { job_id } = await materializeSavedSegment(segmentId, axiosInstance);
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const job = await getSegmentJob(segmentId, job_id, axiosInstance);
    if (_TERMINAL_JOB_STATUSES.has(job.status)) {
      const segment = await fetchSavedSegment(segmentId, axiosInstance);
      return { segment, job };
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return null;
}

export async function deleteSavedSegment(id: string, axiosInstance?: AxiosInstance): Promise<void> {
  const client = axiosInstance || getVoiceFallbackClient();
  await client.delete(`${apiConfig.voiceEndpoints.segments}/${id}`);
}
